import { roundToSignificantFigures } from '../number/format-count';

const SQ_M_PER_SQ_KM = 1_000_000;
const POINT_CLOUD_AREA_SIGNIFICANT_FIGURES = 2;

/**
 * Formats a point cloud area (m²) to match the TRCPS Reality Capture display:
 *   invalid / negative -> "" (caller hides the row)
 *   < 1 m²             -> "< 1 m²"
 *   rounded < 1,000,000 m² -> "~ N m²"   (2 significant figures, no separator)
 *   rounded >= 1,000,000 m² -> "~ N.N km²" (1 decimal place)
 *
 * Note: the m²/km² branch is chosen from the 2-sig-fig rounded value rather
 * than the raw value. This ensures a raw value like 999,500 m² (which rounds
 * to 1,000,000) displays as "~ 1.0 km²" instead of "~ 1000000 m²".
 *
 * The km² formatting also divides `roundedM2` (not the raw value) so the
 * result is free of IEEE 754 artifacts. For example, 1,450,000 m² rounds to
 * 1,500,000 m², which divides cleanly to 1.5 km². Dividing the raw 1,450,000
 * would yield 1.45, and `(1.45).toFixed(1)` evaluates to "1.4" because 1.45
 * is stored as ~1.4499999999999999555.
 */
export function formatPointCloudArea(squareMeters: number): string {
  if (!Number.isFinite(squareMeters) || squareMeters < 0) return '';
  if (squareMeters < 1) return '< 1 m²';
  const roundedM2 = roundToSignificantFigures(
    squareMeters,
    POINT_CLOUD_AREA_SIGNIFICANT_FIGURES
  );
  if (roundedM2 >= SQ_M_PER_SQ_KM) {
    return `~ ${(roundedM2 / SQ_M_PER_SQ_KM).toFixed(1)} km²`;
  }
  return `~ ${roundedM2} m²`;
}
